"use client";

import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cn } from "@/lib/utils";

interface SwitchProps
  extends React.ComponentProps<typeof SwitchPrimitive.Root> {
  size?: "sm" | "lg";
  label?: string;
  containerClassName?: string;
}

/**
 * A customizable toggle switch component built using Radix UI's SwitchPrimitive.
 * Supports small and large sizes, optional labels, and various visual states
 * (checked, unchecked, hover, focus, disabled).
 */
function Switch({
  className,
  containerClassName,
  size = "sm",
  label,
  ...props
}: Readonly<SwitchProps>) {
  return (
    <div className={cn("flex items-center gap-2", containerClassName)}>
      {/* ✅ Switch Root */}
      <SwitchPrimitive.Root
        data-slot="switch"
        className={cn(
          "peer relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-all outline-none disabled:cursor-not-allowed",

          // ✅ UNCHECKED STATE
          "data-[state=unchecked]:bg-switch",
          "data-[state=unchecked]:hover:bg-gray-300",
          "data-[state=unchecked]:focus:bg-switch-focus data-[state=unchecked]:focus:shadow-switch-focus",
          "data-[state=unchecked]:disabled:bg-[var(--fill-base-bg)]",
          "data-[state=unchecked]:disabled:text-[var(--text-gray-light)]",

          // ✅ CHECKED STATE
          "data-[state=checked]:bg-primary-800",
          "data-[state=checked]:shadow-switch-focus",
          "data-[state=checked]:hover:bg-primary-800",
          "data-[state=checked]:focus:bg-primary-800 data-[state=checked]:focus:shadow-switch-focus",
          "data-[state=checked]:disabled:bg-[var(--fill-primary-tonal-normal)]",

          size === "sm" ? "h-5 w-9.5" : "h-6 w-12",
          className,
        )}
        {...props}
      >
        {/* ✅ Switch Thumb */}
        <SwitchPrimitive.Thumb
          data-slot="switch-thumb"
          className={cn(
            "block rounded-full bg-white transition-transform",
            "data-[state=unchecked]:translate-x",
            size === "sm"
              ? "size-4 data-[state=checked]:translate-x-4.5"
              : "size-5 data-[state=checked]:translate-x-6",
          )}
        />
      </SwitchPrimitive.Root>

      {/* ✅ Label */}
      {label && (
        <label
          htmlFor={props.id}
          className={cn(
            "text-sm font-medium text-gray-800 peer-disabled:text-gray-400",
            size === "lg" ? "text-base" : "text-sm",
          )}
        >
          {label}
        </label>
      )}
    </div>
  );
}

export { Switch };
